import { useCallback } from 'react';
import useApi from './useApi';
import { ProductDetail } from '../types/ProductTypes';
import { ProductUpdateResponse } from '../utils/productUpdateConverter';
import { useAppSettings } from '../hooks/useAppSettings';

/**
 * Custom hook for updating product data
 * Uses PUT endpoint: /product/put
 *
 * @example
 * const { updateProduct, isLoading, error, response } = useUpdateProduct();
 *
 * const handleSave = async () => {
 *   try {
 *     await updateProduct(productData);
 *     // Handle success
 *   } catch (err) {
 *     // Handle error
 *   }
 * };
 */
const useUpdateProduct = () => {
    const { state, postApiData } = useApi();
    const { settings: appSettings } = useAppSettings();

    /**
     * Updates a product via the PUT API endpoint
     * @param productData - Complete product data or API payload (any format accepted)
     * @returns Promise<ProductUpdateResponse>
     */
    const updateProduct = useCallback(
        async (productData: ProductDetail | any): Promise<ProductUpdateResponse> => {
            if (!productData || !productData.Id) {
                throw new Error('Product data with valid ID is required');
            }

            try {
                const apiUrl = appSettings?.api;
                if (!apiUrl) {
                    throw new Error('API URL not configured in app settings');
                }

                // Ensure no double slashes in URL
                const endpoint = `${apiUrl}/UpsertProduct`;

                const response = await postApiData({
                    endpoint,
                    data: productData,
                    config: { params: { releaseId: appSettings?.releaseId || '' } },
                });

                return {
                    success: true,
                    data: response,
                    message: 'Product updated successfully',
                };
            } catch (error: any) {
                console.error('Failed to update product:', error);

                const errorMessage = error.response?.data?.message || error.response?.statusText || error.message || 'Failed to update product';

                throw new Error(errorMessage);
            }
        },
        [appSettings, postApiData]
    );

    /**
     * Updates a specific field or section of the product
     * Useful for partial updates without sending the entire payload
     *
     * @param productId - ID of the product to update
     * @param updates - Partial product data to update
     * @param fullProduct - Complete current product data (required for API)
     */
    const updateProductField = useCallback(
        async (productId: number, updates: Partial<ProductDetail>, fullProduct: ProductDetail | any): Promise<ProductUpdateResponse> => {
            // Merge updates with full product data
            const updatedProduct = {
                ...fullProduct,
                ...updates,
                Id: productId,
                LastUpdatedUtc: new Date().toISOString(),
            };

            return updateProduct(updatedProduct);
        },
        [updateProduct]
    );

    return {
        state,
        updateProduct,
        updateProductField,
        isLoading: state.isLoading,
        error: state.error,
        response: state.result as ProductUpdateResponse | null,
    };
};

export default useUpdateProduct;
